import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { ChatMessage, ChatMessageStatus, SendMessageOptions, Citation, ChatLevelUp } from '@/types/chat';
import { MessageRole } from '@/constants';
import { useGetChatQuery } from '@/api';
import { Sender } from '@/api/chat/getChat.ts';
import { useAppDispatch } from '@/store';
import { streamSendChatMessage } from '@/services/chatStreamingSend';
import { chatMessagesCache, getChatKey } from './chatMessagesCache';
import { isUnsavedFailedPrompt, keepUnsavedFailedPrompts } from './unsavedFailedPrompts';
import { useInjectLevelUpSuggestions } from './useInjectLevelUpSuggestions';
import { resolveChatCitations } from '@/ui/features/chat/utils/resolveChatCitations';

interface UseChatMessagesOptions {
  chatId: string;
  setChatQuery?: (query: string) => void;
}

interface ApiNextStepsMessage {
  nextStepsMessageId: string;
  title: string;
  prompt: string;
}

interface ApiDraft {
  generatedDocumentId?: string;
  title: string;
  draftVersionId: string;
}

interface ApiChatMessage {
  messageId: string;
  sender: Sender;
  message: string;
  citations: Record<string, Citation>;
  nextStepsMessages?: ApiNextStepsMessage[];
  levelUp?: ChatLevelUp[];
  drafts?: ApiDraft[];
  status: ChatMessageStatus;
}

/** Convert API chat messages to app ChatMessage format. */
export const convertApiMessagesToChatMessages = (chatMessages: ApiChatMessage[]): ChatMessage[] => {
  return chatMessages.map((msg) => {
    return {
      id: msg.messageId,
      messageId: msg.messageId,
      role: msg.sender === 'USER' ? MessageRole.User : MessageRole.Assistant,
      content: msg.message,
      createdAt: new Date(),
      suggestions: msg.nextStepsMessages?.map((nextStep) => ({
        title: nextStep.title,
        prompt: nextStep.prompt,
      })),
      levelUp: msg.levelUp?.map((e) => ({
        prompt: e.prompt,
      })),
      drafts: msg.drafts || [],
      status: msg.status,
    };
  });
};

/** Preserve client-only fields (attachedDocuments on user msgs, draft on assistant msgs) when hydrating from API. */
const mergeClientFieldsFromPrev = (
  prev: ChatMessage[],
  loaded: ChatMessage[],
  chatLevelCitations?: Record<string, Citation>,
): ChatMessage[] => {
  const prevUserMessages = prev.filter((m) => m.role === MessageRole.User);
  const prevAssistantMessages = prev.filter((m) => m.role === MessageRole.Assistant);
  const hasChatLevelCitations = Boolean(chatLevelCitations && Object.keys(chatLevelCitations).length > 0);
  const UUID_KEY = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  let userIndex = 0;
  let assistantIndex = 0;
  return loaded.map((msg) => {
    if (msg.role === MessageRole.User) {
      const prevUser = prevUserMessages[userIndex];
      userIndex += 1;
      const attachments = prevUser?.attachedDocuments;
      if (attachments && attachments.length > 0) {
        return { ...msg, attachedDocuments: attachments };
      }
      return msg;
    }
    if (msg.role === MessageRole.Assistant) {
      const prevAssistant = prevAssistantMessages[assistantIndex];
      assistantIndex += 1;
      // Prefer API-provided draft (has generatedDocumentId); only fall back to the
      // client-side SSE draft if the API hasn't caught up yet.
      const draftFallback = !msg.drafts && prevAssistant?.drafts ? prevAssistant.drafts : undefined;
      // Preserve in-flight streaming citations on the assistant message until the
      // chat-level `citations` field catches up after stream end.
      const prevCitations = prevAssistant?.citations ?? {};
      const citationsFallback: Record<string, Citation> = {};
      for (const [key, citation] of Object.entries(prevCitations)) {
        if (!UUID_KEY.test(key)) {
          citationsFallback[key] = citation;
        } else if (!hasChatLevelCitations) {
          citationsFallback[key] = citation;
        }
      }
      const citationsFallbackResult = Object.keys(citationsFallback).length > 0 ? citationsFallback : undefined;
      const askForFeedback = prevAssistant?.askForFeedback;
      if (draftFallback || citationsFallbackResult || askForFeedback) {
        return {
          ...msg,
          ...(draftFallback ? { drafts: draftFallback } : {}),
          ...(citationsFallbackResult ? { citations: citationsFallbackResult } : {}),
          ...(askForFeedback ? { askForFeedback } : {}),
        };
      }
      return msg;
    }
    return msg;
  });
};

const EMPTY_MESSAGES: ChatMessage[] = [];

/** Cached chat messages and helpers. Changes go through `chatMessagesCache` so background sends stay in sync. */
export const useChatMessages = ({ chatId, setChatQuery }: UseChatMessagesOptions) => {
  const chatKey = getChatKey(chatId);
  const [messagesByChatKey, setMessagesByChatKey] = useState<Record<string, ChatMessage[]>>(() => ({
    [chatKey]: chatMessagesCache.getMessages(chatId),
  }));
  const [isLoadingByChatKey, setIsLoadingByChatKey] = useState<Record<string, boolean>>(() =>
    chatMessagesCache.isLoading(chatId) ? { [chatKey]: true } : {},
  );
  const [error, setError] = useState<string | null>(chatMessagesCache.getError());
  const isMountedRef = useRef(true);
  const { data: chatData } = useGetChatQuery({ chatId }, { skip: !chatId, refetchOnMountOrArgChange: true });
  const dispatch = useAppDispatch();

  // Keep React state in sync with the module-level cache. This lets streams
  // initiated outside the component tree (Redux middleware) still drive UI.
  useEffect(() => {
    isMountedRef.current = true;
    const unsubscribe = chatMessagesCache.subscribe(({ chatKey: changedChatKey, type }) => {
      if (type !== 'error' && changedChatKey && changedChatKey !== chatKey) {
        return;
      }
      if (!isMountedRef.current) return;

      if (type === 'messages' && changedChatKey) {
        const cached = chatMessagesCache.getMessages(changedChatKey);
        setMessagesByChatKey((prev) => {
          if (prev[changedChatKey] === cached) return prev;
          return { ...prev, [changedChatKey]: cached };
        });
        return;
      }

      if (type === 'loading' && changedChatKey) {
        const cachedIsLoading = chatMessagesCache.isLoading(changedChatKey);
        setIsLoadingByChatKey((prev) => {
          if (Boolean(prev[changedChatKey]) === cachedIsLoading) return prev;
          const next = { ...prev };
          if (cachedIsLoading) {
            next[changedChatKey] = true;
          } else {
            delete next[changedChatKey];
          }
          return next;
        });
        return;
      }

      if (type === 'error') {
        setError(chatMessagesCache.getError());
      }
    });
    return () => {
      unsubscribe();
      isMountedRef.current = false;
    };
  }, [chatKey]);

  const isStreaming = Boolean(isLoadingByChatKey[chatKey]);

  // Hydrate from API while preserving any in-flight streaming output.
  useEffect(() => {
    if (!chatId) {
      chatMessagesCache.setMessages(chatId, []);
      return;
    }

    if (chatData?.chatMessages && chatData.chatId === chatId) {
      const loadedMessages = convertApiMessagesToChatMessages(chatData.chatMessages);

      // Hydrate messages from API (with client-side draft/attachment preservation).
      chatMessagesCache.updateMessages(chatId, (cached) => {
        // The API may not list prompts that failed this session, and they'd throw off the positional merge.
        const prev = cached.filter((m) => !isUnsavedFailedPrompt(m));
        const lastPrevAssistant = [...prev].reverse().find((m) => m.role === MessageRole.Assistant);
        // Mid-stream the API usually lacks the assistant message, so don't hydrate until `finalResultReceived`.
        const finalResultReceived = Boolean(lastPrevAssistant?.finalResultReceived);
        if (
          chatMessagesCache.isLoading(chatId) &&
          (prev.length > loadedMessages.length || (!finalResultReceived && prev.length === loadedMessages.length))
        ) {
          return cached;
        }

        if (lastPrevAssistant?.suggestions?.length || lastPrevAssistant?.levelUp?.length) {
          const merged = mergeClientFieldsFromPrev(prev, [...loadedMessages], chatData.citations);
          for (let i = merged.length - 1; i >= 0; i--) {
            if (merged[i].role === MessageRole.Assistant) {
              merged[i] = {
                ...merged[i],
                ...(lastPrevAssistant.suggestions?.length ? { suggestions: lastPrevAssistant.suggestions } : {}),
                ...(lastPrevAssistant.levelUp?.length ? { levelUp: lastPrevAssistant.levelUp } : {}),
              };
              break;
            }
          }
          return keepUnsavedFailedPrompts(cached, merged);
        }
        return keepUnsavedFailedPrompts(cached, mergeClientFieldsFromPrev(prev, loadedMessages, chatData.citations));
      });
    }
    // Re-run when streaming ends: refetch may complete while `isLoading` is still true,
    // so the guard above keeps optimistic assistant ids until reload without this dep.
  }, [chatData, chatId, isStreaming]);

  useInjectLevelUpSuggestions({
    chatId,
    canEdit: chatData?.canEdit,
    isStreaming: Boolean(isLoadingByChatKey[chatKey]),
    chatDataReadyToken: chatData?.chatMessages,
  });

  const sendMessage = useCallback(
    async (content: string, options?: SendMessageOptions, overrideChatId?: string) => {
      const targetChatId = overrideChatId || chatId;
      const { runFinished } = await streamSendChatMessage({
        dispatch,
        chatId: targetChatId,
        content,
        options,
      });
      if (runFinished && setChatQuery) {
        setChatQuery('');
      }
    },
    [chatId, dispatch, setChatQuery],
  );

  const cancelStreaming = useCallback(() => {
    chatMessagesCache.abort(chatId);
  }, [chatId]);

  const setMessagesHandler = useCallback(
    (newMessages: ChatMessage[]) => {
      chatMessagesCache.setMessages(chatId, newMessages);
    },
    [chatId],
  );

  const messages = messagesByChatKey[chatKey] ?? EMPTY_MESSAGES;

  // Right after a chat switch `chatData` is still the previous chat's response.
  const apiCitations = chatData && chatData.chatId === chatId ? chatData.citations : undefined;
  const chatCitations = useMemo(() => resolveChatCitations(apiCitations, messages), [apiCitations, messages]);

  return {
    messages,
    chatCitations,
    isLoading: Boolean(isLoadingByChatKey[chatKey]),
    error,
    sendMessage,
    cancelStreaming,
    setMessages: setMessagesHandler,
    canEditChat: chatData?.canEdit,
  };
};
